// src/context/ProductDiscountContext.js
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import discountService from '../services/discountService';

const ProductDiscountContext = createContext(null);

export const useProductDiscountContext = () => {
  const context = useContext(ProductDiscountContext);
  if (!context) {
    throw new Error('useProductDiscountContext must be used within a ProductDiscountProvider');
  }
  return context;
};

export const ProductDiscountProvider = ({ children }) => {
  const [productDiscounts, setProductDiscounts] = useState({});
  const [loading, setLoading] = useState(false);
  const fetchedProductsRef = useRef(new Set());
  const fetchedCustomerRef = useRef(undefined);
  const pendingFetchRef = useRef(null);
  const pendingIdsRef = useRef(new Set());

  const fetchDiscountsForProducts = useCallback(async (productIds, customerId = null) => {
    if (!productIds || productIds.length === 0) return;

    if (fetchedCustomerRef.current !== customerId) {
      fetchedCustomerRef.current = customerId;
      fetchedProductsRef.current.clear();
      pendingIdsRef.current.clear();
      setProductDiscounts({});
    }

    const newProductIds = productIds.filter(id => !fetchedProductsRef.current.has(id));
    if (newProductIds.length === 0) return;

    // Accumulate across callers: several page sections (sliders, grids)
    // request discounts within the same tick, and restarting the debounce
    // used to CANCEL the earlier sections' fetches entirely — only the
    // last-mounted section ever showed promo prices.
    newProductIds.forEach(id => pendingIdsRef.current.add(id));

    if (pendingFetchRef.current) {
      clearTimeout(pendingFetchRef.current);
    }

    pendingFetchRef.current = setTimeout(async () => {
      const idsToFetch = Array.from(pendingIdsRef.current);
      pendingIdsRef.current.clear();
      if (idsToFetch.length === 0) return;

      try {
        setLoading(true);

        // Server caps a request at 100 ids — chunk so none are dropped
        for (let i = 0; i < idsToFetch.length; i += 100) {
          const chunk = idsToFetch.slice(i, i + 100);
          const result = await discountService.getDiscountsForProducts(chunk, customerId);

          chunk.forEach(id => fetchedProductsRef.current.add(id));

          setProductDiscounts(prev => ({
            ...prev,
            ...result
          }));
        }
      } catch (error) {
        console.error('[ProductDiscountContext] Error fetching discounts:', error);
      } finally {
        setLoading(false);
        pendingFetchRef.current = null;
      }
    }, 100);
  }, []);

  const getDiscountForProduct = useCallback((productId) => {
    const productData = productDiscounts[productId];
    if (!productData || !productData.discounts || productData.discounts.length === 0) {
      return null;
    }
    return productData.discounts[0];
  }, [productDiscounts]);

  const getDiscountBadge = useCallback((productId) => {
    const discount = getDiscountForProduct(productId);
    if (!discount) return null;
    return discountService.formatDiscountBadge(discount);
  }, [getDiscountForProduct]);

  const getDiscountBadgeInfo = useCallback((productId) => {
    const discount = getDiscountForProduct(productId);
    if (!discount) return null;

    // Check if badge is disabled
    if (discount.badge_enabled === false) return null;

    // Process placeholders in custom text and description
    const processedText = discount.badge_text
      ? discountService.processPlaceholders(discount.badge_text, discount)
      : null;
    const processedDescription = discount.badge_description
      ? discountService.processPlaceholders(discount.badge_description, discount)
      : null;

    // Badge configuration from discount settings
    const badgeConfig = {
      enabled: discount.badge_enabled !== false,
      customText: processedText,
      customDescription: processedDescription,
      color: discount.badge_color || '#DC2626',
      textColor: discount.badge_text_color || '#FFFFFF',
    };

    // For FIXED_PRICE discounts without quantity rules, return structured info
    // so we can show: old price crossed out + new price
    if (discount.discount_type === 'FIXED_PRICE' && !discount.has_quantity_rules && !discount.has_tiers) {
      return {
        type: 'fixed_price',
        newPrice: parseFloat(discount.discount_value),
        originalPrice: discount.original_price ? parseFloat(discount.original_price) : null,
        isMultiPrice: discount.is_multi_price,
        badgeConfig,
      };
    }

    // For other discounts, return the badge string
    return {
      type: 'badge',
      text: badgeConfig.customText || discountService.formatDiscountBadge(discount),
      badgeConfig,
    };
  }, [getDiscountForProduct]);

  const getAllDiscountsForProduct = useCallback((productId) => {
    const productData = productDiscounts[productId];
    if (!productData) return [];
    return productData.discounts || [];
  }, [productDiscounts]);

  const hasComboDiscounts = useCallback(() => {
    return discountService.hasComboDiscounts(productDiscounts);
  }, [productDiscounts]);

  const getComboDiscountExplanations = useCallback(() => {
    return discountService.getComboDiscountExplanations(productDiscounts);
  }, [productDiscounts]);

  const clearCache = useCallback(() => {
    fetchedProductsRef.current.clear();
    fetchedCustomerRef.current = undefined;
    setProductDiscounts({});
  }, []);

  const value = {
    productDiscounts,
    loading,
    fetchDiscountsForProducts,
    getDiscountForProduct,
    getDiscountBadge,
    getDiscountBadgeInfo,
    getAllDiscountsForProduct,
    hasComboDiscounts,
    getComboDiscountExplanations,
    clearCache
  };

  return (
    <ProductDiscountContext.Provider value={value}>
      {children}
    </ProductDiscountContext.Provider>
  );
};

export default ProductDiscountContext;
